import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { captureSentryException } from "@app/helpers/sentry";
import { recoverFromStaleChunk } from "@app/helpers/staleChunkRecovery";
import Error500 from "./Error500";

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  error?: unknown;
  info?: ErrorInfo;
  isRecoveringChunk?: boolean;
};

export default class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {};

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (recoverFromStaleChunk(error)) {
      this.setState({
        error: undefined,
        info: undefined,
        isRecoveringChunk: true,
      });
      return;
    }

    if (import.meta.env.DEV) {
      console.error(error, info.componentStack);
    }

    captureSentryException(error, {
      componentStack: info.componentStack,
    });
    this.setState({ info });
  }

  render() {
    if (this.state.isRecoveringChunk) {
      return null;
    }

    if (this.state.error) {
      return <Error500 error={this.state.error} info={this.state.info} />;
    }

    return this.props.children;
  }
}
